// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";
import { MATCH_DIMENSIONS, DEFAULT_WEIGHTS } from "./matchDimensions";

// What-if tool for the AI 매칭 가중치. Operators tweak the 6 dimension
// weights, hit "재계산 미리보기", and inspect how the ranking would shift
// before they decide whether to commit a permanent change in /settings.
// Strictly preview — no DB writes happen inside this modal.

const CLAMP_MIN = 0;
const CLAMP_MAX = 100;

const clamp = (n) => Math.max(CLAMP_MIN, Math.min(CLAMP_MAX, Number.isFinite(n) ? n : 0));

// COD dimensions ↔ settings.weight_* keys are now 1:1 — the
// RealignMatchingWeightSettings migration unified the settings table on the
// engine's taxonomy, so the settings key is simply `weight_<dimKey>` with no
// translation layer.
const settingsKeyFor = (dimKey: string) => `weight_${dimKey}`;

// Shared fetch helper local to the modal — keeps the tree dependency-free
// of the cod.tsx jsonFetch (which we deliberately don't export to avoid
// coupling components to a screen file).
async function jsonFetchLocal(url, method, body) {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try { payload = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const e = new Error(payload?.error || `HTTP ${res.status}`);
    e.status = res.status;
    e.code = payload?.error;
    throw e;
  }
  return payload;
}
const jsonPost  = (url, body) => jsonFetchLocal(url, "POST",  body);
const jsonPatch = (url, body) => jsonFetchLocal(url, "PATCH", body);

export function WeightAdjustModal({ caseId, currentMatches, onClose, initialWeights, canSavePolicy = false }) {
  useEscapeKey(onClose);
  // 초깃값은 현재 시스템 매칭 정책(설정값) — 없으면 하드코딩 기본값 폴백.
  const [weights, setWeights] = React.useState(initialWeights || DEFAULT_WEIGHTS);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [savedAt, setSavedAt] = React.useState(null);

  const total = React.useMemo(
    () => Object.values(weights).reduce((a, b) => a + b, 0),
    [weights]
  );

  const setW = React.useCallback((key) => (raw) => {
    const v = clamp(Number(raw));
    setWeights(prev => prev[key] === v ? prev : { ...prev, [key]: v });
  }, []);

  const resetDefaults = React.useCallback(() => {
    setWeights({ ...DEFAULT_WEIGHTS });
    setResult(null);
    setErr(null);
  }, []);

  const recalculate = React.useCallback(async () => {
    if (!caseId || total <= 0) {
      setErr("가중치 합이 0보다 커야 합니다.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const j = await jsonPost("/api/v1/matches/recalculate", { case_id: caseId, weights });
      setResult(j);
    } catch (e) {
      setErr(
        e.status === 401 ? "로그인이 필요합니다." :
        e.status === 403 ? "권한이 없습니다." :
        e.status === 422 ? "가중치 값이 유효하지 않습니다." :
        apiErrorMessage(e)
      );
    } finally {
      setBusy(false);
    }
  }, [caseId, weights, total]);

  // Save the current slider values into the persistent matching settings.
  // Maps COD dimension keys to settings.weight_* (see SETTINGS_KEY_MAP).
  const saveAsPolicy = React.useCallback(async () => {
    if (saving || total <= 0) return;
    setSaving(true); setErr(null);
    try {
      const values: Record<string, number> = {};
      for (const d of MATCH_DIMENSIONS) {
        values[settingsKeyFor(d.key)] = weights[d.key] ?? 0;
      }
      await jsonPatch("/api/v1/settings", { section: "matching", values });
      setSavedAt(new Date());
    } catch (e) {
      setErr(
        e.status === 401 ? "로그인이 필요합니다." :
        e.status === 403 ? "시스템 관리자만 영구 적용할 수 있습니다." :
        apiErrorMessage(e)
      );
    } finally {
      setSaving(false);
    }
  }, [saving, total, weights]);

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <div onClick={e=>e.stopPropagation()} className="card"
        style={{width:760, maxHeight:'88vh', padding:0, display:'flex', flexDirection:'column'}}>

        <Header onClose={onClose} total={total} onReset={resetDefaults}/>

        {err && (
          <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>
            {err}
          </div>
        )}

        <div style={{padding:'18px 22px', overflowY:'auto', flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', gap:18}}>
          <WeightSliders weights={weights} setW={setW} total={total}/>
          <ResultPreview result={result} originals={currentMatches}/>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap'}}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            {/* 가중치는 플랫폼 전역값 → 영구 저장은 system_admin 전용(권역 격리).
               권역 관리자에겐 저장 버튼 대신 미리보기 전용임을 안내한다. */}
            {canSavePolicy ? (
              <>
                <button type="button"
                  className="btn btn-ghost btn-md"
                  onClick={saveAsPolicy} disabled={saving || total <= 0}
                  title="현재 슬라이더 값을 시스템 매칭 정책으로 영구 저장합니다">
                  {saving ? '저장 중…' : (savedAt ? '✓ 저장됨' : '이 가중치 저장')}
                </button>
                {savedAt && (
                  <span className="muted" style={{fontSize:11}}>
                    {savedAt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 저장
                  </span>
                )}
              </>
            ) : (
              <span className="muted" style={{fontSize:11.5}}>
                ⓘ 미리보기 전용 — 영구 적용(전 권역 공통)은 시스템 관리자 권한입니다.
              </span>
            )}
            <a href="/settings#matching" style={{fontSize:11.5, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>
              ↗ 설정에서 보기
            </a>
          </div>
          <div style={{display:'flex', gap:8}}>
            <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
            <button type="button" className="btn btn-primary btn-md" onClick={recalculate} disabled={busy || total <= 0}>
              {busy ? '계산 중…' : '재계산 미리보기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function Header({ onClose, total, onReset }) {
  const totalOk = total === 100;
  return (
    <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
      <div>
        <div className="h3">AI 가중치 미리보기</div>
        <div className="muted" style={{fontSize:11, marginTop:2}}>
          현재 가중치 합계 <b style={{color: totalOk ? 'var(--green-600)' : 'var(--rose-500)'}}>{total}%</b>
          {!totalOk && <span style={{marginLeft:6}}>· 100%가 아니어도 비율로 정규화됩니다</span>}
        </div>
      </div>
      <div style={{display:'flex', gap:6, alignItems:'center'}}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>기본값</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
      </div>
    </div>
  );
}

function WeightSliders({ weights, setW, total }) {
  return (
    <div>
      <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:10, letterSpacing:'0.04em'}}>
        6개 차원 가중치
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:14}}>
        {MATCH_DIMENSIONS.map(d => {
          const v = weights[d.key] ?? 0;
          const share = total > 0 ? Math.round((v / total) * 100) : 0;
          return (
            <div key={d.key}>
              <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:12.5, fontWeight:700}}>{d.label}</div>
                  {d.description && (
                    <div className="muted" style={{fontSize:10.5}}>{d.description}</div>
                  )}
                </div>
                <div className="mono" style={{fontSize:14, fontWeight:800, color:'var(--orange-600)'}}>
                  {v}<span className="muted" style={{fontSize:10, fontWeight:600}}>%</span>
                  {total !== 100 && total > 0 && (
                    <span className="muted" style={{fontSize:10, fontWeight:600, marginLeft:6}}>({share}% 비중)</span>
                  )}
                </div>
              </div>
              <input
                type="range" min={CLAMP_MIN} max={CLAMP_MAX}
                value={v}
                onChange={e => setW(d.key)(e.target.value)}
                aria-label={`${d.label} 가중치`}
                style={{width:'100%', marginTop:6, accentColor:'var(--orange-500)'}}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResultPreview({ result, originals }) {
  if (!result) {
    return (
      <div style={{display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', background:'var(--bg-sub)', borderRadius:12, padding:'30px 20px', textAlign:'center'}}>
        <div className="muted" style={{fontSize:12, fontWeight:700, marginBottom:6}}>아직 재계산 안 함</div>
        <div className="muted" style={{fontSize:11.5, lineHeight:1.55, maxWidth:240}}>
          좌측에서 가중치를 조정한 뒤 <b>재계산 미리보기</b>를 눌러 순위 변화를 확인하세요.
        </div>
      </div>
    );
  }

  // Detect rank movement vs the original ordering currently shown on the screen.
  const originalRankById = new Map((originals || []).map(m => [m.id, m.rank]));

  return (
    <div>
      <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:10, letterSpacing:'0.04em'}}>
        재계산 결과 ({result.matches.length}건)
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:8}}>
        {result.matches.map(row => {
          const origRank = originalRankById.get(row.id) ?? row.original_rank;
          const newRank = row.new_rank;
          const rankDelta = origRank - newRank; // +n = moved up
          const scoreDelta = row.new_score - row.original_score;
          return (
            <div key={row.id} style={{
              padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-soft)',
              background: newRank === 1 ? 'var(--orange-50)' : 'var(--surface)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 99,
                background: newRank === 1 ? 'var(--orange-500)' : 'var(--bg-deep)',
                color: newRank === 1 ? '#fff' : 'var(--ink-2)',
                display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13,
              }}>{newRank}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12.5, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                  {row.care_manager.name}
                  <span className="muted" style={{fontWeight:500, fontSize:10.5, marginLeft:6}}>
                    · {row.care_manager.qualification}
                  </span>
                </div>
                <div className="muted" style={{fontSize:10.5, marginTop:2}}>
                  {row.care_manager.organization_name || '소속 미배정'}
                </div>
              </div>
              <RankDeltaBadge delta={rankDelta}/>
              <div style={{textAlign:'right', minWidth:64}}>
                <div className="mono" style={{fontSize:15, fontWeight:800, color:'var(--orange-600)', lineHeight:1}}>
                  {row.new_score}
                </div>
                <div className="mono" style={{fontSize:10, color: scoreDelta >= 0 ? 'var(--green-600)' : 'var(--rose-500)', marginTop:2}}>
                  {scoreDelta >= 0 ? '+' : ''}{scoreDelta} <span className="muted">(이전 {row.original_score})</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RankDeltaBadge({ delta }) {
  if (delta === 0) return <span className="muted mono" style={{fontSize:11, minWidth:36, textAlign:'center'}}>—</span>;
  const up = delta > 0;
  const color = up ? 'green' : 'rose';
  const arrow = up ? '↑' : '↓';
  return (
    <span className={"badge badge-" + color}
      style={{fontSize:10.5, fontWeight:700, minWidth:46, textAlign:'center'}}
      title={`이전 대비 ${Math.abs(delta)}단계 ${up ? '상승' : '하락'}`}>
      {arrow} {Math.abs(delta)}
    </span>
  );
}
